/**
 * Decodes a JWT's payload segment without verifying the signature. That is
 * deliberate: the tokens decoded here come from our own session route and are
 * only read to decide whether to ask for a fresh one, never to make an
 * authorisation decision. Returns null for anything that isn't a decodable
 * JWT.
 */
export const decodeJwtPayload = (
  token: string
): Record<string, unknown> | null => {
  const payloadSegment = token.split('.')[1];
  if (!payloadSegment) return null;

  try {
    const base64 = payloadSegment.replace(/-/g, '+').replace(/_/g, '/');
    // base64url strips `=` padding; restore it rather than lean on atob's
    // forgiving-base64 behaviour.
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    // atob yields a binary string; round-trip through TextDecoder so
    // non-ASCII claim values (e.g. names) survive the decode.
    const bytes = Uint8Array.from(atob(padded), (char) => char.charCodeAt(0));
    const parsed = JSON.parse(new TextDecoder().decode(bytes));
    return typeof parsed === 'object' && parsed !== null ? parsed : null;
  } catch {
    return null;
  }
};

// A token this close to its expiry is treated as already expired, so a mint
// that presents it does not lose a race against the clock in transit.
const EXPIRY_BUFFER_IN_MS = 30 * 1000;

/**
 * Whether the token's `exp` claim has passed (or falls inside the buffer).
 *
 * Expiry must be provable to count: an undecodable token or one without an
 * `exp` claim reports false, because the fallback for "cannot tell" is to
 * present the token and let the server be the judge, not to force a refresh
 * round trip on every call.
 */
export const isAccessTokenExpired = (token: string): boolean => {
  const expiry = decodeJwtPayload(token)?.exp;
  if (typeof expiry !== 'number') return false;
  return expiry * 1000 <= Date.now() + EXPIRY_BUFFER_IN_MS;
};
